import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getDatabase,
  ref as databaseRef,
  get,
  set,
  update,
} from "firebase/database";
import {
  FaCheck,
  FaEye,
  FaFileExcel,
  FaSave,
  FaTimes,
  FaExclamationCircle,
  FaFolderOpen,
  FaSyncAlt,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import "./dispatch.css";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* ------------------------------------------
   SHARED HELPERS
------------------------------------------ */

const pad2 = (value) => String(value).padStart(2, "0");

const getDateKey = (date = new Date()) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const getMonthName = (date = new Date()) => MONTH_NAMES[date.getMonth()];

const formatDate = (dateKey, timestamp) => {
  const date = timestamp
    ? new Date(timestamp)
    : new Date(`${dateKey}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return dateKey || "Unknown date";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  return Object.keys(value)
    .filter((key) => /^\d+$/.test(key))
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => value[key]);
};

const normalizeHeader = (header) =>
  String(header ?? "")
    .trim()
    .toLowerCase()
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ");

/**
 * Parse billing dates that arrive as day-month-year
 * (DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY) or as native Date / ISO.
 * Returns YYYY-MM-DD or null.
 */
const parseToDateKey = (raw) => {
  if (raw == null || raw === "") return null;

  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return getDateKey(raw);
  }

  const str = String(raw).trim();
  if (!str) return null;

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  // DD/MM/YYYY  DD-MM-YYYY  DD.MM.YYYY
  const dmy = str.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = Number(dmy[3]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${pad2(month)}-${pad2(day)}`;
    }
  }

  // Fallback – try native parser (ISO etc.)
  const native = new Date(str);
  if (!Number.isNaN(native.getTime()) && str.length >= 8) {
    return getDateKey(native);
  }

  return null;
};

const HEADER_ALIASES = {
  customerCode: [
    "customer code",
    "customer code id",
    "code",
    "code id",
    "customer id",
  ],
  companyName: ["customer name", "company name", "customer", "company"],
  grade: ["grade", "material grade"],
  dispatchQty: [
    "delivered qty",
    "delivered quantity",
    "dispatch qty",
    "dispatch quantity",
    "quantity",
  ],
  invoiceAmt: ["invoice amt", "invoice amount", "invoice value", "amount"],
  truckNo: ["truck no", "truck number", "truck no."],
  shippingAddress: [
    "shipped destination",
    "shipping address",
    "shipping destination",
    "destination",
    "ship to",
    "site",
    "site address",
  ],
  dispatchDate: ["billing date", "dispatch date"],
};

const REQUIRED_FIELDS = ["customerCode", "companyName", "grade", "dispatchQty"];

const STANDARD_HEADERS = [
  "Customer Code",
  "Company Name",
  "Grade",
  "Dispatch Qty",
  "Invoice Amt",
  "Truck No",
  "Shipped Destination",
  "Billing Date",
];

const buildColumnIndexes = (headers) => {
  const normalized = headers.map(normalizeHeader);
  return Object.fromEntries(
    Object.entries(HEADER_ALIASES).map(([field, aliases]) => [
      field,
      normalized.findIndex((h) => aliases.includes(h)),
    ]),
  );
};

const valueAt = (row, columnIndexes, field) => {
  const index = columnIndexes[field];
  return index === -1 ? "" : String(row[index] ?? "").trim();
};

const extractDispatchRecords = (headers, rows) => {
  const columnIndexes = buildColumnIndexes(headers);

  const missing = REQUIRED_FIELDS.filter(
    (field) => columnIndexes[field] === -1,
  );
  if (missing.length > 0) {
    throw new Error(
      `Required Excel columns not found: ${missing.join(", ")}`,
    );
  }

  const records = rows
    .map((row, rowIndex) => ({
      customerCode: valueAt(row, columnIndexes, "customerCode"),
      companyName: valueAt(row, columnIndexes, "companyName"),
      grade: valueAt(row, columnIndexes, "grade"),
      dispatchQty: valueAt(row, columnIndexes, "dispatchQty"),
      invoiceAmt: valueAt(row, columnIndexes, "invoiceAmt"),
      truckNo: valueAt(row, columnIndexes, "truckNo"),
      shippingAddress: valueAt(row, columnIndexes, "shippingAddress"),
      dispatchDate: valueAt(row, columnIndexes, "dispatchDate"),
      archiveRowIndex: rowIndex,
    }))
    .filter(
      (r) =>
        r.customerCode && r.companyName && r.grade && r.dispatchQty,
    );

  if (records.length === 0) {
    throw new Error(
      "No usable dispatch records were found in the Excel file.",
    );
  }

  return records;
};

const recordToRow = (record) => [
  record.customerCode ?? "",
  record.companyName ?? "",
  record.grade ?? "",
  record.dispatchQty ?? "",
  record.invoiceAmt ?? "",
  record.truckNo ?? "",
  record.shippingAddress ?? "",
  record.dispatchDate ?? "",
];

/* ------------------------------------------
   COMPONENT
------------------------------------------ */
const DispatchFiles = ({
  onClose,
  onDispatchDataReady,
  orders,           // optional – matching stays in UserOrders
  onAutoUpdateOrder, // optional – apply stays in UserOrders
}) => {
  const fileInputRef = useRef(null);
  const savedTimerRef = useRef(null);

  /* ---------- state ---------- */
  const [archives, setArchives] = useState([]);
  const [isLoadingArchives, setIsLoadingArchives] = useState(true);
  const [archiveError, setArchiveError] = useState("");

  const currentMonth = getMonthName();

  const [selectedArchiveMonth, setSelectedArchiveMonth] =
    useState(currentMonth);
  const [selectedArchiveDate, setSelectedArchiveDate] = useState(""); // "" = all dates

  const [isUploading, setIsUploading] = useState(false);
  const [uploadSaved, setUploadSaved] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [viewArchive, setViewArchive] = useState(null);
  const [editableHeaders, setEditableHeaders] = useState([]);
  const [editableRows, setEditableRows] = useState([]);
  const [fullMonthRecords, setFullMonthRecords] = useState([]);
  const [isSavingArchive, setIsSavingArchive] = useState(false);
  const [editorError, setEditorError] = useState("");

  /* ------------------------------------------
       LOAD – supports both flat & old nested structures
    ------------------------------------------ */
  const loadArchives = useCallback(async () => {
    try {
      setIsLoadingArchives(true);
      setArchiveError("");

      const db = getDatabase();
      const snapshot = await get(databaseRef(db, "dispatchArchive"));
      const root = snapshot.exists() ? snapshot.val() : {};
      const entries = [];

      Object.entries(root || {}).forEach(([monthName, value]) => {
        if (monthName.startsWith("_") || !value || typeof value !== "object") {
          return;
        }

        // NEW flat structure: dispatchArchive/{MonthName} = { records: [...] }
        if (
          Array.isArray(value.records) ||
          (value.records && typeof value.records === "object")
        ) {
          const records = normalizeArray(value.records);
          if (records.length === 0) return;

          const billingDates = [
            ...new Set(
              records
                .map((r) => parseToDateKey(r.dispatchDate) || r.dispatchDate)
                .filter(Boolean),
            ),
          ].sort((a, b) => b.localeCompare(a));

          entries.push({
            monthName,
            fileName: value.fileName || "Dispatch Excel",
            uploadedAt: Number(value.uploadedAt) || 0,
            lastModified: Number(value.lastModified || value.uploadedAt) || 0,
            headers: STANDARD_HEADERS,
            rows: records.map(recordToRow),
            records,
            billingDates,
            path: `dispatchArchive/${monthName}`,
          });
          return;
        }

        // OLD nested structure (migration support)
        const allRecords = [];
        let latestModified = 0;
        let earliestUploaded = Number.MAX_SAFE_INTEGER;
        let lastFileName = "Dispatch Excel";

        Object.entries(value).forEach(([dateKey, dayRecord]) => {
          if (!dayRecord || typeof dayRecord !== "object") return;
          const dayRecords = normalizeArray(dayRecord.records);
          if (dayRecords.length === 0) return;

          allRecords.push(...dayRecords);
          latestModified = Math.max(
            latestModified,
            Number(dayRecord.lastModified || dayRecord.uploadedAt) || 0,
          );
          earliestUploaded = Math.min(
            earliestUploaded,
            Number(dayRecord.uploadedAt) || Date.now(),
          );
          if (dayRecord.fileName) lastFileName = dayRecord.fileName;
        });

        if (allRecords.length === 0) return;

        const billingDates = [
          ...new Set(
            allRecords
              .map((r) => parseToDateKey(r.dispatchDate) || r.dispatchDate)
              .filter(Boolean),
          ),
        ].sort((a, b) => b.localeCompare(a));

        entries.push({
          monthName,
          fileName: lastFileName,
          uploadedAt:
            earliestUploaded === Number.MAX_SAFE_INTEGER ? 0 : earliestUploaded,
          lastModified: latestModified,
          headers: STANDARD_HEADERS,
          rows: allRecords.map(recordToRow),
          records: allRecords,
          billingDates,
          path: `dispatchArchive/${monthName}`,
        });
      });

      entries.sort(
        (a, b) =>
          b.lastModified - a.lastModified ||
          MONTH_NAMES.indexOf(b.monthName) - MONTH_NAMES.indexOf(a.monthName),
      );

      setArchives(entries);
    } catch (error) {
      console.error("Failed to load dispatch archives:", error);
      setArchiveError(
        error?.message ||
          "Unable to load archived records. Please try again.",
      );
    } finally {
      setIsLoadingArchives(false);
    }
  }, []);

  /* ---------- derived values ---------- */
  const availableArchiveMonths = useMemo(() => {
    const months = archives.map((a) => a.monthName);
    return months.sort(
      (a, b) => MONTH_NAMES.indexOf(b) - MONTH_NAMES.indexOf(a),
    );
  }, [archives]);

  const availableArchiveDates = useMemo(() => {
    const archive = archives.find((a) => a.monthName === selectedArchiveMonth);
    return archive?.billingDates || [];
  }, [archives, selectedArchiveMonth]);

  const filteredArchives = useMemo(() => {
    return archives.filter(
      (archive) => archive.monthName === selectedArchiveMonth,
    );
  }, [archives, selectedArchiveMonth]);

  /* ---------- effects ---------- */

  // 1. Initial load + cleanup
  useEffect(() => {
    loadArchives();
    return () => {
      if (savedTimerRef.current) {
        window.clearTimeout(savedTimerRef.current);
      }
    };
  }, [loadArchives]);

  // 2. Keep selected month valid after data loads
  useEffect(() => {
    if (archives.length === 0) return;

    const hasSelected = archives.some(
      (a) => a.monthName === selectedArchiveMonth,
    );

    if (!hasSelected) {
      const prefer =
        archives.find((a) => a.monthName === currentMonth)?.monthName ||
        archives[0].monthName;
      setSelectedArchiveMonth(prefer);
      setSelectedArchiveDate("");
    }
  }, [archives, selectedArchiveMonth, currentMonth]);

  // 3. Keep selected date valid when month changes
  useEffect(() => {
    if (
      selectedArchiveDate &&
      !availableArchiveDates.includes(selectedArchiveDate)
    ) {
      setSelectedArchiveDate("");
    }
  }, [availableArchiveDates, selectedArchiveDate]);

  /* ------------------------------------------
       UPLOAD – group by month, store one node per month
    ------------------------------------------ */
  const openFilePicker = () => {
    if (isUploading) return;
    setUploadError("");
    setUploadSaved(false);
    fileInputRef.current?.click();
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || isUploading) return;

    setUploadError("");
    setUploadSaved(false);
    if (savedTimerRef.current) {
      window.clearTimeout(savedTimerRef.current);
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(extension)) {
      setUploadError("Select an .xlsx, .xls, or .csv file.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError("The file exceeds the maximum size of 5 MB.");
      return;
    }
    if (file.size === 0) {
      setUploadError("The selected file is empty.");
      return;
    }

    setIsUploading(true);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, {
        type: "array",
        cellDates: true,
      });

      if (!workbook.SheetNames.length) {
        throw new Error("The workbook contains no worksheets.");
      }

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!worksheet || !worksheet["!ref"]) {
        throw new Error("The first worksheet contains no data.");
      }

      const sheetRows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
        raw: false,
        blankrows: false,
      });

      if (!sheetRows.length) {
        throw new Error("The selected worksheet is empty.");
      }

      const headers = normalizeArray(sheetRows[0]).map((h, i) => {
        const value = String(h ?? "").trim();
        return value || `Column ${i + 1}`;
      });

      if (!headers.length) {
        throw new Error("No column headers were found.");
      }

      const rows = sheetRows.slice(1).map((row) => {
        const normalized = normalizeArray(row);
        return headers.map((_, index) => {
          const value = normalized[index] ?? "";
          return value instanceof Date ? value.toISOString() : value;
        });
      });

      const now = new Date();
      const fallbackMonthName = getMonthName(now);
      const fallbackDateKey = getDateKey(now);
      const timestamp = Date.now();

      const dispatchRecords = extractDispatchRecords(headers, rows);

      // Group rows by the month of their billing date
      const monthGroups = {};

      for (const rec of dispatchRecords) {
        const parsedKey = parseToDateKey(rec.dispatchDate);
        const dateKey = parsedKey || fallbackDateKey;

        const [y, m] = dateKey.split("-").map(Number);
        const monthName =
          y && m ? MONTH_NAMES[m - 1] : fallbackMonthName;

        const normalised = {
          ...rec,
          dispatchDate: dateKey, // always store YYYY-MM-DD
          sourceFileName: file.name,
        };

        if (!monthGroups[monthName]) {
          monthGroups[monthName] = [];
        }
        monthGroups[monthName].push(normalised);
      }

      // Write one node per month
      const db = getDatabase();

      for (const [monthName, records] of Object.entries(monthGroups)) {
        const archivePath = `dispatchArchive/${monthName}`;

        const existingSnapshot = await get(databaseRef(db, archivePath));
        const existing = existingSnapshot.exists()
          ? existingSnapshot.val()
          : {};

        await set(databaseRef(db, archivePath), {
          monthName,
          fileName: file.name,
          uploadedAt: Number(existing.uploadedAt) || timestamp,
          lastModified: timestamp,
          rowCount: records.length,
          columnCount: 8,
          records,
        });
      }

      // Point UI to the first month we just wrote (prefer current)
      const writtenMonths = Object.keys(monthGroups);
      const prefer = writtenMonths.includes(fallbackMonthName)
        ? fallbackMonthName
        : writtenMonths[0];

      setSelectedArchiveMonth(prefer);
      setSelectedArchiveDate(""); // show full month

      await loadArchives();

      setUploadSaved(true);
      savedTimerRef.current = window.setTimeout(() => {
        setUploadSaved(false);
      }, 2200);
    } catch (error) {
      console.error("Failed to save dispatch Excel:", error);
      setUploadError(
        error?.message ||
          "The file could not be converted or saved to Firebase.",
      );
    } finally {
      setIsUploading(false);
    }
  };

  /* ------------------------------------------
       EDITOR
       - Always opens the full month node
       - If a billing date is selected, only those rows are shown
       - On save we merge the edited rows back into the full month
    ------------------------------------------ */
  const openArchive = (archive) => {
    const headers = [...archive.headers];
    const truckNoHeaders = ["truck no", "truck number", "truck no."];
    const truckNoIndex = headers.findIndex((h) =>
      truckNoHeaders.includes(String(h).trim().toLowerCase()),
    );

    if (truckNoIndex === -1) {
      headers.push("Truck No");
    } else {
      headers[truckNoIndex] = "Truck No";
    }

    // Keep the complete month data for later merge
    setFullMonthRecords(archive.records);

    // Decide which rows to show
    let rowsToShow = archive.rows;
    if (selectedArchiveDate) {
      rowsToShow = archive.rows.filter((row, idx) => {
        const rec = archive.records[idx];
        const key = parseToDateKey(rec?.dispatchDate) || rec?.dispatchDate;
        return key === selectedArchiveDate;
      });
    }

    setViewArchive(archive);
    setEditableHeaders(headers);
    setEditableRows(
      rowsToShow.map((row) => {
        const normalized = normalizeArray(row);
        return headers.map((_, i) => normalized[i] ?? "");
      }),
    );
    setEditorError("");
  };

  const handleCellChange = (rowIndex, columnIndex, value) => {
    setEditableRows((prev) =>
      prev.map((row, i) => {
        if (i !== rowIndex) return row;
        const updated = [...row];
        updated[columnIndex] = value;
        return updated;
      }),
    );
  };

  const saveArchive = async () => {
    if (!viewArchive || isSavingArchive) return;

    try {
      setIsSavingArchive(true);
      setEditorError("");

      const columnIndexes = buildColumnIndexes(editableHeaders);

      // Build the records that were visible / edited
      const editedSubset = editableRows.map((row, rowIndex) => {
        const rec = {
          customerCode: valueAt(row, columnIndexes, "customerCode"),
          companyName: valueAt(row, columnIndexes, "companyName"),
          grade: valueAt(row, columnIndexes, "grade"),
          dispatchQty: valueAt(row, columnIndexes, "dispatchQty"),
          invoiceAmt: valueAt(row, columnIndexes, "invoiceAmt"),
          truckNo: valueAt(row, columnIndexes, "truckNo"),
          shippingAddress: valueAt(row, columnIndexes, "shippingAddress"),
          dispatchDate: valueAt(row, columnIndexes, "dispatchDate"),
          archiveRowIndex: rowIndex,
        };
        const parsed = parseToDateKey(rec.dispatchDate);
        if (parsed) rec.dispatchDate = parsed;
        return rec;
      });

      // Merge: keep all records that were NOT part of the filtered view,
      // then replace the filtered ones with the edited subset.
      let finalRecords;
      if (selectedArchiveDate) {
        const otherDates = fullMonthRecords.filter((r) => {
          const key = parseToDateKey(r.dispatchDate) || r.dispatchDate;
          return key !== selectedArchiveDate;
        });
        finalRecords = [...otherDates, ...editedSubset];
      } else {
        // Full month was shown → just use the edited list
        finalRecords = editedSubset;
      }

      const db = getDatabase();
      await update(databaseRef(db, viewArchive.path), {
        records: finalRecords,
        rowCount: finalRecords.length,
        columnCount: 8,
        lastModified: Date.now(),
      });

      setViewArchive(null);
      setEditableHeaders([]);
      setEditableRows([]);
      setFullMonthRecords([]);
      await loadArchives();
    } catch (error) {
      console.error("Failed to save edited archive:", error);
      setEditorError(
        error?.message || "Unable to save changes. Please try again.",
      );
    } finally {
      setIsSavingArchive(false);
    }
  };

  const closeEditor = () => {
    if (isSavingArchive) return;
    setViewArchive(null);
    setEditableHeaders([]);
    setEditableRows([]);
    setFullMonthRecords([]);
    setEditorError("");
  };

  /* ------------------------------------------
       MATCH – send FULL records so UserOrders can
       fill dispatch qty, truck numbers, address, etc.
    ------------------------------------------ */
const handleMatchArchive = (archive) => {
  if (!archive || !Array.isArray(archive.records)) {
    setArchiveError(
      "This archive does not contain valid dispatch records.",
    );
    return;
  }

  // Respect the current date filter when matching
  let source = archive.records;
  if (selectedArchiveDate) {
    source = source.filter((r) => {
      const key = parseToDateKey(r.dispatchDate) || r.dispatchDate;
      return key === selectedArchiveDate;
    });
  }

  // FULL records so UserOrders can show Excel rows + apply qty/trucks
  const records = source
    .map((record) => ({
      customerCode: String(record.customerCode ?? "").trim(),
      companyName: String(record.companyName ?? "").trim(),
      grade: String(record.grade ?? "").trim(),
      // qty – both names for handleDispatchAutoUpdate
      deliveredQty: String(record.dispatchQty ?? "").trim(),
      dispatchQty: String(record.dispatchQty ?? "").trim(),
      truckNo: String(record.truckNo ?? "").trim(),
      shippingAddress: String(record.shippingAddress ?? "").trim(),
      invoiceAmt: String(record.invoiceAmt ?? "").trim(),
      dispatchDate: String(record.dispatchDate ?? "").trim(),
    }))
    .filter(
      (record) =>
        record.customerCode &&
        record.companyName &&
        record.grade &&
        (record.deliveredQty || record.dispatchQty),
    );

  if (records.length === 0) {
    setArchiveError(
      "No usable dispatch rows were found for the current filter.",
    );
    return;
  }

  setArchiveError("");
  if (typeof onDispatchDataReady === "function") {
    onDispatchDataReady(records);
  }
};

  /* ------------------------------------------
       RENDER
    ------------------------------------------ */
  return (
    <div className="dispatch-files-container">
      {/* PAGE HEADER */}
      <header className="dispatch-files-header">
        <div className="dispatch-files-heading">
          <div className="dispatch-files-heading-icon">
            <FaFolderOpen />
          </div>
          <div>
            <h2>Dispatch Archive</h2>
            <p>Manage and review your saved dispatch spreadsheets.</p>
          </div>
        </div>

        <button
          type="button"
          className="dispatch-files-close-btn"
          onClick={onClose}
          title="Close archive"
          aria-label="Close archive"
        >
          <FaTimes />
        </button>
      </header>

      {/* UPLOAD */}
      <section className="dispatch-archive-upload-section">
        <div className="dispatch-archive-upload-copy">
          <div className="dispatch-archive-upload-icon">
            <FaFileExcel />
          </div>
          <div>
            <h3>Import dispatch records</h3>
            <p>
              Rows are grouped by the month of their billing date and stored
              under that month.
            </p>
          </div>
        </div>

        <div className="dispatch-archive-upload-actions">
          <button
            type="button"
            className={[
              "dispatch-archive-upload-btn",
              isUploading ? "is-uploading" : "",
              uploadSaved ? "is-saved" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={openFilePicker}
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <span className="dispatch-archive-spinner" />
                Saving…
              </>
            ) : uploadSaved ? (
              <>
                <FaCheck />
                Saved
              </>
            ) : (
              <>
                <FaFileExcel />
                Upload Excel
              </>
            )}
          </button>

          <input
            ref={fileInputRef}
            id="dispatch-archive-excel-input"
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleExcelUpload}
            disabled={isUploading}
            hidden
          />
        </div>

        {isUploading && (
          <div className="dispatch-archive-loading-indicator">
            <span />
          </div>
        )}

        {uploadError && (
          <div className="dispatch-files-error" role="alert">
            <FaExclamationCircle />
            <span>{uploadError}</span>
          </div>
        )}

        <div className="dispatch-archive-upload-footer">
          <span>Supported: XLSX, XLS, CSV</span>
          <span>Maximum file size: 5 MB</span>
          <span>Stored month-wise</span>
        </div>
      </section>

      {/* ARCHIVE LIST + FILTERS */}
      <section className="dispatch-archive-list-section">
        <div className="dispatch-archive-list-heading">
          <div>
            <h3>Saved archives</h3>
            <p>
              One entry per month. Filter by billing date to view only that
              day’s rows.
            </p>
          </div>

          <button
            type="button"
            className="dispatch-archive-refresh-btn"
            onClick={loadArchives}
            disabled={isLoadingArchives}
            aria-label="Refresh archives"
            title="Refresh archives"
          >
            <FaSyncAlt className={isLoadingArchives ? "is-spinning" : ""} />
            Refresh
          </button>
        </div>

        {/* Filter bar – only rendered when data exists */}
        {!isLoadingArchives && archives.length > 0 && (
          <div className="dispatch-archive-filter-bar">
            <label className="dispatch-archive-filter-label">
              Month
              <select
                value={selectedArchiveMonth}
                onChange={(e) => {
                  setSelectedArchiveMonth(e.target.value);
                  setSelectedArchiveDate(""); // full month
                }}
                className="dispatch-archive-filter-select"
              >
                {availableArchiveMonths.map((month) => (
                  <option key={month} value={month}>
                    {month}
                    {month === currentMonth ? " (current)" : ""}
                  </option>
                ))}
              </select>
            </label>

            <label className="dispatch-archive-filter-label">
              Billing Date
              <select
                value={selectedArchiveDate}
                onChange={(e) => setSelectedArchiveDate(e.target.value)}
                className="dispatch-archive-filter-select"
              >
                <option value="">All dates (full month)</option>
                {availableArchiveDates.map((dateKey) => (
                  <option key={dateKey} value={dateKey}>
                    {dateKey}
                  </option>
                ))}
              </select>
            </label>

            <span className="dispatch-archive-filter-count">
              {filteredArchives.length === 0
                ? "0 archives"
                : selectedArchiveDate
                  ? `Showing ${selectedArchiveDate}`
                  : `Full ${selectedArchiveMonth}`}
            </span>
          </div>
        )}

        {archiveError && (
          <div className="dispatch-files-error" role="alert">
            <FaExclamationCircle />
            <span>{archiveError}</span>
          </div>
        )}

        {isLoadingArchives ? (
          <div className="dispatch-files-state">
            <span className="dispatch-archive-spinner" />
            <span>Loading archives…</span>
          </div>
        ) : archives.length === 0 ? (
          <div className="dispatch-files-empty">
            <div className="dispatch-files-empty-icon">
              <FaFolderOpen />
            </div>
            <h4>No saved archives</h4>
            <p>Uploaded dispatch spreadsheets will appear here.</p>
          </div>
        ) : filteredArchives.length === 0 ? (
          <div className="dispatch-files-empty">
            <div className="dispatch-files-empty-icon">
              <FaFolderOpen />
            </div>
            <h4>No data for this month</h4>
            <p>Select another month that contains records.</p>
          </div>
        ) : (
          <div className="dispatch-files-list">
            {filteredArchives.map((archive) => (
              <article className="dispatch-file-row" key={archive.path}>
                <div className="dispatch-file-info">
                  <div className="dispatch-file-icon-box">
                    <FaFileExcel />
                  </div>

                  <div className="dispatch-file-details">
                    <h4 className="dispatch-file-name">
                      {archive.monthName}
                      {selectedArchiveDate && (
                        <span className="dispatch-file-date-badge">
                          {selectedArchiveDate}
                        </span>
                      )}
                    </h4>
                    <p className="dispatch-file-original-name">
                      {archive.fileName}
                    </p>

                    <div className="dispatch-file-metadata">
                      <span>
                        {selectedArchiveDate
                          ? `${
                              archive.records.filter((r) => {
                                const k =
                                  parseToDateKey(r.dispatchDate) ||
                                  r.dispatchDate;
                                return k === selectedArchiveDate;
                              }).length
                            } rows (filtered)`
                          : `${archive.rows.length} rows`}
                      </span>
                      <span className="dispatch-file-metadata-dot" />
                      <span>
                        {archive.billingDates.length} billing date
                        {archive.billingDates.length === 1 ? "" : "s"}
                      </span>
                      <span className="dispatch-file-metadata-dot" />
                      <span>
                        Modified{" "}
                        {formatDate(
                          getDateKey(new Date(archive.lastModified)),
                          archive.lastModified,
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="dispatch-file-actions">
                  <button
                    type="button"
                    className="dispatch-file-match-btn"
                    onClick={() => handleMatchArchive(archive)}
                    disabled={isLoadingArchives || isUploading}
                    title="Send filtered rows to User Orders for matching"
                  >
                    <FaSyncAlt />
                    Match
                  </button>

                  <button
                    type="button"
                    className="dispatch-file-view-btn"
                    onClick={() => openArchive(archive)}
                  >
                    <FaEye />
                    View records
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* EDITOR MODAL */}
      {viewArchive && (
        <div
          className="dispatch-excel-editor-overlay"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !isSavingArchive
            ) {
              closeEditor();
            }
          }}
        >
          <section
            className="dispatch-excel-editor-modal dispatch-archive-editor-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dispatch-editor-title"
          >
            <header className="dispatch-excel-editor-header">
              <div className="dispatch-editor-heading">
                <div className="dispatch-file-icon-box">
                  <FaFileExcel />
                </div>
                <div>
                  <h3 id="dispatch-editor-title">
                    {viewArchive.monthName}
                    {selectedArchiveDate && (
                      <span className="dispatch-file-date-badge">
                        {selectedArchiveDate}
                      </span>
                    )}
                  </h3>
                  <p>{viewArchive.fileName}</p>
                </div>
              </div>

              <button
                type="button"
                className="dispatch-files-close-btn"
                onClick={closeEditor}
                disabled={isSavingArchive}
                aria-label="Close editor"
                title="Close editor"
              >
                <FaTimes />
              </button>
            </header>

            {editorError && (
              <div className="dispatch-files-error" role="alert">
                <FaExclamationCircle />
                <span>{editorError}</span>
              </div>
            )}

            <div className="dispatch-excel-editor-content">
              {editableHeaders.length === 0 ? (
                <div className="dispatch-files-empty">
                  <h4>No columns available</h4>
                  <p>This archive does not contain any column headers.</p>
                </div>
              ) : (
                <div className="dispatch-excel-editor-table-container">
                  <table className="dispatch-excel-editor-table dispatch-excel-editor-table-content-width">
                    <thead>
                      <tr>
                        <th>No.</th>
                        {editableHeaders
                          .map((header, index) => ({ header, index }))
                          .filter(
                            ({ header }) =>
                              String(header).trim().toLowerCase() !== "unit",
                          )
                          .map(({ header, index }) => (
                            <th key={`${header}-${index}`}>{header}</th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {editableRows.map((row, rowIndex) => (
                        <tr key={`archive-row-${rowIndex}`}>
                          <td className="dispatch-row-number-cell">
                            {rowIndex + 1}
                          </td>
                          {editableHeaders
                            .map((header, columnIndex) => ({
                              header,
                              columnIndex,
                            }))
                            .filter(
                              ({ header }) =>
                                String(header).trim().toLowerCase() !== "unit",
                            )
                            .map(({ header, columnIndex }) => (
                              <td
                                key={`archive-cell-${rowIndex}-${columnIndex}`}
                              >
                                <input
                                  type="text"
                                  value={row[columnIndex] ?? ""}
                                  aria-label={`${header}, row ${rowIndex + 1}`}
                                  onChange={(event) =>
                                    handleCellChange(
                                      rowIndex,
                                      columnIndex,
                                      event.target.value,
                                    )
                                  }
                                />
                              </td>
                            ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {editableRows.length === 0 && (
                    <div className="dispatch-editor-no-rows">
                      No data rows match the current filter.
                    </div>
                  )}
                </div>
              )}
            </div>

            <footer className="dispatch-excel-editor-footer">
              <div className="dispatch-editor-row-count">
                {editableRows.length}{" "}
                {editableRows.length === 1 ? "record" : "records"}
                {selectedArchiveDate && " (filtered)"}
              </div>

              <div className="dispatch-editor-footer-actions">
                <button
                  type="button"
                  className="dispatch-excel-editor-cancel-btn"
                  onClick={closeEditor}
                  disabled={isSavingArchive}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="dispatch-excel-editor-save-btn"
                  onClick={saveArchive}
                  disabled={
                    isSavingArchive || editableHeaders.length === 0
                  }
                >
                  {isSavingArchive ? (
                    <>
                      <span className="dispatch-archive-spinner" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Save changes
                    </>
                  )}
                </button>
              </div>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
};

export default DispatchFiles;